import React, { useState } from 'react';
import { Building2, ArrowDownCircle, ArrowUpCircle, AlertCircle, Plus, MapPin, Camera, Check, AlertTriangle, Calendar, Edit, ClipboardList, ChevronRight } from 'lucide-react';
import { AddPropertyModal } from './modals/AddPropertyModal';
import { EditPropertyModal } from './modals/EditPropertyModal';
import { NewEsigenzaModal } from './modals/NewEsigenzaModal';
interface Property {
  id: number;
  name: string;
  address: string;
  zone: string;
  sqm: number;
  rooms: number;
  bathrooms: number;
  status: 'Attiva' | 'Manutenzione';
  checkIn: string | null;
  checkOut: string | null;
  issues: string[];
  checklist: string[];
  imageCount: number;
  city?: string;
}
const initialProperties: Property[] = [{
  id: 1,
  name: 'Appartamento Prati',
  address: 'Via Cola di Rienzo 28',
  zone: 'Roma, Prati',
  city: 'Roma',
  sqm: 85,
  rooms: 3,
  bathrooms: 2,
  status: 'Attiva',
  checkIn: '12 Feb, 15:00',
  checkOut: '14 Feb, 10:00',
  issues: ['Perdita rubinetto bagno'],
  checklist: ['Cambio biancheria', 'Pulizia bagni', 'Aspirapolvere', 'Cucina e elettrodomestici', 'Rifornimento kit cortesia'],
  imageCount: 3
}, {
  id: 2,
  name: 'Loft Centro Storico',
  address: 'Via del Corso 150',
  zone: 'Roma, Centro',
  city: 'Roma',
  sqm: 90,
  rooms: 3,
  bathrooms: 2,
  status: 'Attiva',
  checkIn: '11 Feb, 16:00',
  checkOut: '13 Feb, 11:00',
  issues: ['Citofono intermittente', 'Finestra camera bloccata'],
  checklist: ['Cambio biancheria', 'Pulizia bagni', 'Aspirapolvere', 'Cucina e elettrodomestici', 'Rifornimento kit cortesia'],
  imageCount: 4
}, {
  id: 3,
  name: 'Bilocale Trastevere',
  address: 'Via della Lungaretta 12',
  zone: 'Roma, Trastevere',
  city: 'Roma',
  sqm: 55,
  rooms: 2,
  bathrooms: 1,
  status: 'Manutenzione',
  checkIn: null,
  checkOut: null,
  issues: ['Boiler da sostituire'],
  checklist: ['Cambio biancheria', 'Pulizia bagni', 'Aspirapolvere', 'Rifornimento kit cortesia'],
  imageCount: 3
}, {
  id: 4,
  name: 'Suite Testaccio',
  address: 'Via Marmorata 32',
  zone: 'Roma, Testaccio',
  city: 'Roma',
  sqm: 65,
  rooms: 2,
  bathrooms: 1,
  status: 'Attiva',
  checkIn: '13 Feb, 14:00',
  checkOut: '15 Feb, 10:00',
  issues: [],
  checklist: ['Cambio biancheria', 'Pulizia bagni', 'Aspirapolvere', 'Cucina e elettrodomestici'],
  imageCount: 3
}];
export function HostPropertiesPage() {
  const [properties, setProperties] = useState<Property[]>(initialProperties);
  // Modal States
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showNewEsigenzaModal, setShowNewEsigenzaModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  // Handlers
  const handleAddProperty = (newProperty: any) => {
    const property: Property = {
      ...newProperty,
      id: Math.max(...properties.map(p => p.id)) + 1,
      zone: `Roma, ${newProperty.zone}`
    };
    setProperties([...properties, property]);
    setShowAddPropertyModal(false);
  };
  const handleEditProperty = (updatedProperty: Property) => {
    setProperties(properties.map(p => p.id === updatedProperty.id ? updatedProperty : p));
    setShowEditPropertyModal(false);
    setSelectedPropertyId(null);
  };
  const handleCreateEsigenza = (esigenza: any) => {
    console.log('Nuova esigenza creata:', esigenza);
    // Here you would typically send this to an API or update an esigenze state
    alert('Esigenza creata con successo!');
    setShowNewEsigenzaModal(false);
    setSelectedPropertyId(null);
  };
  const openEditModal = (id: number) => {
    setSelectedPropertyId(id);
    setShowEditPropertyModal(true);
  };
  const openEsigenzaModal = (id: number | null = null) => {
    setSelectedPropertyId(id);
    setShowNewEsigenzaModal(true);
  };
  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  return <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6 lg:space-y-8" data-id="element-569">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-570">
        <div data-id="element-571">
          <h1 className="text-2xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-572">
            Le Mie Proprietà
          </h1>
          <p className="text-sm text-gray-500" data-id="element-573">
            Gestisci le tue proprietà e i dettagli per le pulizie
          </p>
        </div>
        <button onClick={() => setShowAddPropertyModal(true)} className="flex items-center gap-2 bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm px-5 py-3 lg:px-4 lg:py-2.5 transition-colors shadow-sm min-h-[48px] lg:min-h-[44px]" data-id="element-574">
          <Plus size={18} data-id="element-575" />
          <span data-id="element-576">Aggiungi Proprietà</span>
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4" data-id="element-577">
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm" data-id="element-578">
          <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-deep" data-id="element-579">
            <Building2 size={20} data-id="element-580" />
          </div>
          <div data-id="element-581">
            <p className="text-2xl font-bold text-navy font-montserrat" data-id="element-582">
              {properties.length}
            </p>
            <p className="text-sm lg:text-xs text-gray-500 font-medium" data-id="element-583">
              Proprietà Attive
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm" data-id="element-584">
          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600" data-id="element-585">
            <ArrowDownCircle size={20} data-id="element-586" />
          </div>
          <div data-id="element-587">
            <p className="text-2xl font-bold text-navy font-montserrat" data-id="element-588">2</p>
            <p className="text-sm lg:text-xs text-gray-500 font-medium" data-id="element-589">
              Check-in Oggi
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm" data-id="element-590">
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-591">
            <ArrowUpCircle size={20} data-id="element-592" />
          </div>
          <div data-id="element-593">
            <p className="text-2xl font-bold text-navy font-montserrat" data-id="element-594">3</p>
            <p className="text-sm lg:text-xs text-gray-500 font-medium" data-id="element-595">
              Check-out Oggi
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-start gap-3 shadow-sm" data-id="element-596">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600" data-id="element-597">
            <AlertCircle size={20} data-id="element-598" />
          </div>
          <div data-id="element-599">
            <p className="text-2xl font-bold text-navy font-montserrat" data-id="element-600">4</p>
            <p className="text-sm lg:text-xs text-gray-500 font-medium" data-id="element-601">
              Esigenze Aperte
            </p>
          </div>
        </div>
      </div>

      {/* Property Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5" data-id="element-602">
        {properties.map(property => <div key={property.id} onClick={() => openEditModal(property.id)} className="group relative bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-all cursor-pointer" data-id="element-603">
            {/* Header: Name + Status */}
            <div className="flex justify-between items-start mb-1 pr-8" data-id="element-604">
              <h2 className="text-base font-semibold font-montserrat text-navy" data-id="element-605">
                {property.name}
              </h2>
              <span className={`flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border ${property.status === 'Attiva' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-606">
                {property.status}
              </span>
            </div>

            {/* Address & Zone */}
            <div className="text-sm text-gray-500 mb-4" data-id="element-607">
              {property.address} ·{' '}
              {property.zone.split(', ')[1] || property.zone}
            </div>

            {/* Metrics */}
            <div className="flex items-center gap-3 text-xs text-gray-400 mb-4 font-medium" data-id="element-608">
              <span data-id="element-609">{property.sqm}mq</span>
              <span data-id="element-610">·</span>
              <span data-id="element-611">{property.rooms} camere</span>
              <span data-id="element-612">·</span>
              <span data-id="element-613">{property.bathrooms} bagni</span>
            </div>

            {/* Bottom Info: Check-in & Issues */}
            <div className="space-y-2 min-h-[20px]" data-id="element-614">
              {property.checkIn ? <div className="flex items-center gap-2 text-xs font-medium text-navy" data-id="element-615">
                    <Calendar size={14} className="text-gray-400" data-id="element-616" />
                    <span data-id="element-617">Check-in: {property.checkIn}</span>
                  </div> : <div className="h-5" data-id="element-618"></div> // Spacer to keep alignment if needed, or just collapse
          }

              {property.issues.length > 0 && <div className="flex items-center gap-2 text-xs font-medium text-red-600" data-id="element-619">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600" data-id="element-620"></div>
                  <span data-id="element-621">{property.issues.length} problema segnalato</span>
                </div>}
            </div>

            {/* Navigation Hint */}
            <div className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-300 group-hover:text-teal-deep transition-colors" data-id="element-622">
              <ChevronRight size={20} data-id="element-623" />
            </div>
          </div>)}
      </div>

      {/* Modals */}
      <AddPropertyModal isOpen={showAddPropertyModal} onClose={() => setShowAddPropertyModal(false)} onSave={handleAddProperty} data-id="element-624" />

      <EditPropertyModal isOpen={showEditPropertyModal} onClose={() => {
      setShowEditPropertyModal(false);
      setSelectedPropertyId(null);
    }} onSave={handleEditProperty} property={selectedProperty} data-id="element-625" />

      <NewEsigenzaModal isOpen={showNewEsigenzaModal} onClose={() => {
      setShowNewEsigenzaModal(false);
      setSelectedPropertyId(null);
    }} onSave={handleCreateEsigenza} properties={properties} preSelectedPropertyId={selectedPropertyId} data-id="element-626" />
    </div>;
}